import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { contactsAPI, notesAPI, activitiesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import {
  Users,
  Pencil,
  Trash2,
  Building2,
  Mail,
  Phone,
  Smartphone,
  FileText,
  History,
  ArrowLeft,
  Send,
  Calendar,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const getRoleTagClass = (tag) => {
  switch (tag) {
    case 'client':
      return 'badge-client';
    case 'venue':
      return 'badge-venue';
    case 'technical':
    case 'freelancer':
      return 'badge-vendor';
    case 'AVF Staff':
      return 'badge-partner';
    default:
      return 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400';
  }
};

const ContactDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [contact, setContact] = useState(null);
  const [notes, setNotes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [linkedEvents, setLinkedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [contactRes, notesRes, activitiesRes, eventsRes] = await Promise.all([
        contactsAPI.get(id),
        notesAPI.list('contact', id),
        activitiesAPI.list('contact', id),
        contactsAPI.getEvents(id),
      ]);
      setContact(contactRes.data);
      setNotes(notesRes.data);
      setActivities(activitiesRes.data);
      setLinkedEvents(eventsRes.data);
    } catch (error) {
      toast.error('Failed to load contact');
      navigate('/contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      await notesAPI.create({
        entity_type: 'contact',
        entity_id: id,
        content: newNote,
      });
      setNewNote('');
      loadData();
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleDelete = async () => {
    try {
      await contactsAPI.delete(id);
      toast.success('Contact deleted');
      navigate('/contacts');
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
      </div>
    );
  }

  if (!contact) return null;

  return (
    <div className="space-y-6" data-testid="contact-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/contacts">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
              <Users className="h-8 w-8 text-cyan-400" />
              {contact.contact_name}
            </h1>
            {contact.title && (
              <p className="text-lg text-muted-foreground mt-1">{contact.title}</p>
            )}
            <div className="flex flex-wrap gap-2 mt-2">
              {contact.role_tags.map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className={`${getRoleTagClass(tag)}`}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 ml-12 sm:ml-0">
          <Button variant="outline" asChild data-testid="edit-contact-btn">
            <Link to={`/contacts/${id}/edit`}>
              <Pencil className="h-4 w-4 mr-2" />
              Edit
            </Link>
          </Button>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setShowDelete(true)}
              data-testid="delete-contact-btn"
            >
              <Trash2 className="h-4 w-4 mr-2 text-destructive" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Info */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {contact.organization_name && (
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted/50">
                    <Building2 className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Organization</p>
                    <Link
                      to={`/organizations/${contact.organization_id}`}
                      className="font-medium hover:text-primary transition-colors"
                    >
                      {contact.organization_name}
                    </Link>
                  </div>
                </div>
              )}

              {contact.email && (
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted/50">
                    <Mail className="h-4 w-4 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <a
                      href={`mailto:${contact.email}`}
                      className="font-medium hover:text-primary transition-colors"
                    >
                      {contact.email}
                    </a>
                  </div>
                </div>
              )}

              {contact.phone && (
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted/50">
                    <Phone className="h-4 w-4 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <a
                      href={`tel:${contact.phone}`}
                      className="font-medium hover:text-primary transition-colors"
                    >
                      {contact.phone}
                    </a>
                  </div>
                </div>
              )}

              {contact.mobile && (
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-muted/50">
                    <Smartphone className="h-4 w-4 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Mobile</p>
                    <a
                      href={`tel:${contact.mobile}`}
                      className="font-medium hover:text-primary transition-colors"
                    >
                      {contact.mobile}
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Notes */}
          {contact.notes && (
            <Card className="border-border/50 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap">{contact.notes}</p>
              </CardContent>
            </Card>
          )}

          {/* Linked Events */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Calendar className="h-5 w-5 text-emerald-400" />
                Linked Events
              </CardTitle>
            </CardHeader>
            <CardContent>
              {linkedEvents.length > 0 ? (
                <Table data-testid="linked-events-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Client</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linkedEvents.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>
                          <Link
                            to={`/events/${event.id}`}
                            className="font-medium hover:text-primary transition-colors"
                            data-testid={`event-link-${event.id}`}
                          >
                            {event.event_name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {event.role_in_event && (
                            <Badge variant="outline" className="badge-partner">
                              {event.role_in_event}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {event.start_date
                            ? format(new Date(event.start_date), 'MMM d, yyyy')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              event.status === 'completed'
                                ? 'badge-partner'
                                : event.status === 'in_progress'
                                ? 'badge-vendor'
                                : event.status === 'confirmed'
                                ? 'badge-venue'
                                : 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400'
                            }
                          >
                            {event.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {event.client_name || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground text-center py-6">
                  No linked events found
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Notes & Activity */}
        <div className="space-y-6">
          {/* Add Note */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Add Note
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                placeholder="Write a note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="bg-background/50 min-h-[100px]"
                data-testid="note-input"
              />
              <Button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="w-full"
                data-testid="add-note-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Add Note
              </Button>
            </CardContent>
          </Card>

          {/* Notes History */}
          {notes.length > 0 && (
            <Card className="border-border/50 bg-card/50">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Note History</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[250px] pr-4">
                  <div className="space-y-4">
                    {notes.map((note) => (
                      <div key={note.id} className="p-3 rounded-lg bg-background/50">
                        <p className="whitespace-pre-wrap text-sm">{note.content}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {note.created_by_name} •{' '}
                          {formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Activity History */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <History className="h-5 w-5 text-cyan-400" />
                History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[250px] pr-4">
                {activities.length > 0 ? (
                  <div className="space-y-3">
                    {activities.map((activity) => (
                      <div key={activity.id} className="text-sm">
                        <p>
                          <span className="font-medium">{activity.action}</span>
                          {' - '}
                          {activity.description}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {activity.created_by_name} •{' '}
                          {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
                        </p>
                        <Separator className="mt-3" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-6">No history yet</p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{contact.contact_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ContactDetail;
